import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/LandingEspresso";
import Today from "@/pages/Today";
import Dashboard from "@/pages/Dashboard";
import Pipeline from "@/pages/Pipeline";
import Vault from "@/pages/Vault";
import Swipe from "@/pages/Swipe";
import CalendarPage from "@/pages/Calendar";
import Goals from "@/pages/Goals";
import SettingsPage from "@/pages/Settings";
import Analytics from "@/pages/Analytics";
import Posts from "@/pages/Posts";
import Admin from "@/pages/Admin";
import Scripts from "@/pages/Scripts";
import HowItWorks from "@/pages/HowItWorks";
import Pricing from "@/pages/Pricing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Refund from "@/pages/Refund";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import Cookies from "@/pages/Cookies";
import FAQ from "@/pages/FAQ";
import Shipping from "@/pages/Shipping";
import NotFound from "@/pages/NotFound";
import FeedbackButton from "@/components/FeedbackButton";
import CoachTour from "@/components/CoachTour";
import AdminLayout from "@/components/AdminLayout";
import { initAnalytics, identify, track, EVENTS } from "@/lib/analytics";
import AuthCallback from "@/pages/AuthCallback";
import PreviewIndex from "@/pages/PreviewIndex";
import LandingV1 from "@/pages/LandingV1";
import LandingV2 from "@/pages/LandingV2";
import LandingV3 from "@/pages/LandingV3";
import LandingHybrid from "@/pages/LandingHybrid";
import LandingPremium from "@/pages/LandingPremium";
import LandingEspresso from "@/pages/LandingEspresso";
import LandingForest from "@/pages/LandingForest";
import Layout from "@/components/Layout";
import CookieBanner from "@/components/CookieBanner";
import { Toaster, toast } from "sonner";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500 font-mono text-xs uppercase tracking-[0.3em]">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace state={{ from: location }} />;
  return (
    <Layout>
      {children}
      <FeedbackButton />
      <CoachTour />
    </Layout>
  );
}

// Admin gets its own clean layout — no sidebar, no "New idea" button.
// Pricing is public, but when a user is logged in we wrap it in the regular
// sidebar Layout so the nav-pricing item keeps them oriented.
function MaybeAuthLayout({ children }) {
  const { user } = useAuth();
  if (user) {
    return <Layout>{children}</Layout>;
  }
  return children;
}


function AdminProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
        <div className="text-sm" style={{ color: "var(--ink-55)" }}>Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace state={{ from: location }} />;
  return (
    <AdminLayout>
      {children}
    </AdminLayout>
  );
}

function PublicHome() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-neutral-950" />;
  if (user) return <Navigate to="/today" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous detection BEFORE any auth check (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/preview" element={<PreviewIndex />} />
      <Route path="/preview/v1" element={<LandingV1 />} />
      <Route path="/preview/v2" element={<LandingV2 />} />
      <Route path="/preview/v3" element={<LandingV3 />} />
      <Route path="/preview/hybrid" element={<LandingHybrid />} />
      <Route path="/preview/premium" element={<LandingPremium />} />
      <Route path="/preview/espresso" element={<LandingEspresso />} />
      <Route path="/preview/forest" element={<LandingForest />} />
      <Route path="/today" element={<ProtectedRoute><Today /></ProtectedRoute>} />
      <Route path="/dashboard" element={<Navigate to="/today" replace />} />
      <Route path="/insights" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><Pipeline /></ProtectedRoute>} />
      <Route path="/vault" element={<ProtectedRoute><Vault /></ProtectedRoute>} />
      <Route path="/swipe" element={<ProtectedRoute><Swipe /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/posts" element={<ProtectedRoute><Posts /></ProtectedRoute>} />
      <Route path="/scripts" element={<ProtectedRoute><Scripts /></ProtectedRoute>} />
      <Route path="/how-it-works" element={<ProtectedRoute><HowItWorks /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminProtectedRoute><Admin /></AdminProtectedRoute>} />
      <Route path="/pricing" element={<MaybeAuthLayout><Pricing /></MaybeAuthLayout>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund" element={<Refund />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/shipping" element={<Shipping />} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/service-worker.js").then((reg) => {
          // Check for updates immediately + every minute
          reg.update().catch(() => {});
          setInterval(() => reg.update().catch(() => {}), 60_000);
          // When a new worker installs, prompt the user to refresh.
          reg.addEventListener("updatefound", () => {
            const nw = reg.installing;
            if (!nw) return;
            nw.addEventListener("statechange", () => {
              if (nw.state === "installed" && navigator.serviceWorker.controller) {
                // A new version is ready in the background. Tell the user
                // instead of silently reloading mid-task.
                toast("A fresh PintaFlow is ready", {
                  description: "Tap refresh to load the latest fixes.",
                  duration: Infinity,
                  action: {
                    label: "Refresh",
                    onClick: () => {
                      nw.postMessage("skip-waiting");
                      // controllerchange handler below will reload the page.
                    },
                  },
                });
              }
            });
          });
        }).catch(() => {});
        // Reload page when the new SW takes over (user accepted the toast).
        let refreshing = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (refreshing) return;
          refreshing = true;
          window.location.reload();
        });
      });
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <CookieBanner />
        <Toaster
          position="top-center"
          richColors
          closeButton
          theme="dark"
          toastOptions={{
            style: {
              background: "var(--surface)",
              border: "1px solid rgba(216,168,87,0.40)",
              color: "var(--ink)",
              fontFamily: "Inter, sans-serif",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
